export const FLOORPLAN_DRAFT_SCHEMA_VERSION = "ambifi.floorplan.draft.v1";
export const FLOORPLAN_AUTHORING_RENDITIONS_SCHEMA_VERSION = "ambifi.floorplan.authoring-renditions.v1";

const LEGACY_FORMAT_VERSION = 2;
const SOURCE_FORMAT = "claude-floorplan-project";
const DRIVE_RESOURCE_TYPE = "resource/external/drive";
const SHA256_PATTERN = /^[a-f0-9]{64}$/;
const DATA_URL_PATTERN = /^data:/i;
const SOURCE_FIELDS = new Set(["format", "formatVersion", "fileName"]);
const ASSET_DESCRIPTOR_FIELDS = new Set(["id", "sha256", "mimeType", "byteLength", "fileName", "roles", "resource"]);
const AUTHORING_RENDITIONS_FIELDS = new Set(["schemaVersion", "defaultProfile", "profiles"]);
const STUDIO_PROFILE_FIELDS = new Set(["maxDimension", "assets"]);
const RENDITION_DESCRIPTOR_FIELDS = new Set(["id", "sha256", "mimeType", "byteLength", "fileName", "width", "height", "resource"]);

const EMBEDDED_ASSET_FIELDS = {
	imageData: {
		assetIdField: "imageAssetId",
		role: "artwork"
	},
	origImageData: {
		assetIdField: "origImageAssetId",
		role: "originalArtwork"
	},
	maskBehindData: {
		assetIdField: "maskBehindAssetId",
		role: "maskBehind"
	},
	maskOverData: {
		assetIdField: "maskOverAssetId",
		role: "maskOver"
	}
};

const ASSET_ID_FIELDS = new Set(Object.values(EMBEDDED_ASSET_FIELDS).map((field) => field.assetIdField));
const ROLE_ORDER = ["artwork", "originalArtwork", "maskBehind", "maskOver"];

const SHA256_CONSTANTS = [
	0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
	0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
	0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
	0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
	0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
	0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
	0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
	0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2
];

function isObject(value) {
	return value !== null && typeof value === "object" && !Array.isArray(value);
}

function hasOwn(value, key) {
	return Object.prototype.hasOwnProperty.call(value, key);
}

function isNonEmptyString(value) {
	return typeof value === "string" && value.length > 0;
}

function isDriveRevision(value) {
	return Number.isInteger(value) || isNonEmptyString(value);
}

function isCanonicalDriveResource(resource) {
	return isObject(resource)
		&& isNonEmptyString(resource.id)
		&& resource.type === DRIVE_RESOURCE_TYPE
		&& isNonEmptyString(resource.guid)
		&& isDriveRevision(resource.revision)
		&& isNonEmptyString(resource.scope);
}

function basename(fileName) {
	const parts = String(fileName || "").split(/[\\/]/);
	return parts[parts.length - 1] || "floorplan.floorplanproject.json";
}

function extensionForMimeType(mimeType) {
	const extensions = {
		"image/gif": "gif",
		"image/jpeg": "jpg",
		"image/png": "png",
		"image/svg+xml": "svg",
		"image/tiff": "tif",
		"image/webp": "webp"
	};
	return extensions[mimeType] || "bin";
}

function base64Value(charCode) {
	if (charCode >= 65 && charCode <= 90) {
		return charCode - 65;
	}
	if (charCode >= 97 && charCode <= 122) {
		return charCode - 71;
	}
	if (charCode >= 48 && charCode <= 57) {
		return charCode + 4;
	}
	if (charCode === 43) {
		return 62;
	}
	if (charCode === 47) {
		return 63;
	}
	return -1;
}

function decodeBase64(encodedValue, owner) {
	let encoded = encodedValue;
	if (/\s/.test(encoded)) {
		encoded = encoded.replace(/\s/g, "");
	}
	if (!encoded || encoded.length % 4 === 1 || !/^[A-Za-z0-9+/]*={0,2}$/.test(encoded)) {
		throw new Error(`${owner} contains invalid base64 image data.`);
	}
	if (encoded.indexOf("=") !== -1 && encoded.indexOf("=") < encoded.length - 2) {
		throw new Error(`${owner} contains invalid base64 padding.`);
	}

	const paddingNeeded = (4 - (encoded.length % 4)) % 4;
	if (paddingNeeded > 0) {
		encoded += "=".repeat(paddingNeeded);
	}
	const padding = encoded.endsWith("==") ? 2 : encoded.endsWith("=") ? 1 : 0;
	const bytes = new Uint8Array((encoded.length / 4) * 3 - padding);
	let byteIndex = 0;

	for (let index = 0; index < encoded.length; index += 4) {
		const first = base64Value(encoded.charCodeAt(index));
		const second = base64Value(encoded.charCodeAt(index + 1));
		const third = encoded[index + 2] === "=" ? 0 : base64Value(encoded.charCodeAt(index + 2));
		const fourth = encoded[index + 3] === "=" ? 0 : base64Value(encoded.charCodeAt(index + 3));
		if (first < 0 || second < 0 || third < 0 || fourth < 0) {
			throw new Error(`${owner} contains invalid base64 image data.`);
		}
		if (byteIndex < bytes.length) {
			bytes[byteIndex++] = (first << 2) | (second >>> 4);
		}
		if (byteIndex < bytes.length) {
			bytes[byteIndex++] = ((second & 15) << 4) | (third >>> 2);
		}
		if (byteIndex < bytes.length) {
			bytes[byteIndex++] = ((third & 3) << 6) | fourth;
		}
	}
	return bytes;
}

function parseImageDataUrl(dataUrl, owner) {
	if (typeof dataUrl !== "string" || !DATA_URL_PATTERN.test(dataUrl)) {
		throw new Error(`${owner} must be an embedded image data URL.`);
	}
	const commaIndex = dataUrl.indexOf(",");
	if (commaIndex < 0) {
		throw new Error(`${owner} is not a valid data URL.`);
	}
	const metadata = dataUrl.slice(5, commaIndex).split(";");
	const mimeType = String(metadata.shift() || "").toLowerCase();
	if (!mimeType.startsWith("image/")) {
		throw new Error(`${owner} must contain image data.`);
	}
	if (!metadata.some((value) => value.toLowerCase() === "base64")) {
		throw new Error(`${owner} must use base64 encoding.`);
	}
	const bytes = decodeBase64(dataUrl.slice(commaIndex + 1), owner);
	if (bytes.byteLength === 0) {
		throw new Error(`${owner} contains an empty image.`);
	}
	return { bytes, mimeType };
}

function rotateRight(value, count) {
	return (value >>> count) | (value << (32 - count));
}

function fallbackSha256(bytes) {
	const source = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
	const paddedLength = Math.ceil((source.byteLength + 9) / 64) * 64;
	const padded = new Uint8Array(paddedLength);
	padded.set(source);
	padded[source.byteLength] = 0x80;
	const bitLength = source.byteLength * 8;
	const highBitLength = Math.floor(bitLength / 0x100000000);
	const lowBitLength = bitLength >>> 0;
	const lengthOffset = paddedLength - 8;
	padded[lengthOffset] = highBitLength >>> 24;
	padded[lengthOffset + 1] = highBitLength >>> 16;
	padded[lengthOffset + 2] = highBitLength >>> 8;
	padded[lengthOffset + 3] = highBitLength;
	padded[lengthOffset + 4] = lowBitLength >>> 24;
	padded[lengthOffset + 5] = lowBitLength >>> 16;
	padded[lengthOffset + 6] = lowBitLength >>> 8;
	padded[lengthOffset + 7] = lowBitLength;

	let hash = [
		0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a,
		0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19
	];
	const words = new Uint32Array(64);

	for (let offset = 0; offset < padded.length; offset += 64) {
		for (let index = 0; index < 16; index += 1) {
			const wordOffset = offset + index * 4;
			words[index] = (
				(padded[wordOffset] << 24)
				| (padded[wordOffset + 1] << 16)
				| (padded[wordOffset + 2] << 8)
				| padded[wordOffset + 3]
			) >>> 0;
		}
		for (let index = 16; index < 64; index += 1) {
			const value15 = words[index - 15];
			const value2 = words[index - 2];
			const sigma0 = rotateRight(value15, 7) ^ rotateRight(value15, 18) ^ (value15 >>> 3);
			const sigma1 = rotateRight(value2, 17) ^ rotateRight(value2, 19) ^ (value2 >>> 10);
			words[index] = (words[index - 16] + sigma0 + words[index - 7] + sigma1) >>> 0;
		}

		let [a, b, c, d, e, f, g, h] = hash;
		for (let index = 0; index < 64; index += 1) {
			const sum1 = rotateRight(e, 6) ^ rotateRight(e, 11) ^ rotateRight(e, 25);
			const choice = (e & f) ^ (~e & g);
			const temp1 = (h + sum1 + choice + SHA256_CONSTANTS[index] + words[index]) >>> 0;
			const sum0 = rotateRight(a, 2) ^ rotateRight(a, 13) ^ rotateRight(a, 22);
			const majority = (a & b) ^ (a & c) ^ (b & c);
			const temp2 = (sum0 + majority) >>> 0;
			h = g;
			g = f;
			f = e;
			e = (d + temp1) >>> 0;
			d = c;
			c = b;
			b = a;
			a = (temp1 + temp2) >>> 0;
		}
		hash = [
			(hash[0] + a) >>> 0,
			(hash[1] + b) >>> 0,
			(hash[2] + c) >>> 0,
			(hash[3] + d) >>> 0,
			(hash[4] + e) >>> 0,
			(hash[5] + f) >>> 0,
			(hash[6] + g) >>> 0,
			(hash[7] + h) >>> 0
		];
	}

	return hash.map((value) => value.toString(16).padStart(8, "0")).join("");
}

function bytesToHex(bytes) {
	return Array.from(bytes, (value) => value.toString(16).padStart(2, "0")).join("");
}

function normalizeHashResult(value) {
	if (typeof value === "string") {
		const normalized = value.toLowerCase();
		if (!SHA256_PATTERN.test(normalized)) {
			throw new Error("The floorplan asset hasher returned an invalid SHA-256 value.");
		}
		return normalized;
	}
	if (value instanceof ArrayBuffer || ArrayBuffer.isView(value)) {
		const bytes = value instanceof ArrayBuffer
			? new Uint8Array(value)
			: new Uint8Array(value.buffer, value.byteOffset, value.byteLength);
		if (bytes.byteLength !== 32) {
			throw new Error("The floorplan asset hasher returned an invalid SHA-256 digest.");
		}
		return bytesToHex(bytes);
	}
	throw new Error("The floorplan asset hasher did not return a SHA-256 digest.");
}

export async function sha256FloorplanAsset(bytes, options = {}) {
	const source = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
	const cryptoProvider = options.crypto === undefined
		? (typeof window !== "undefined" ? window.crypto : null)
		: options.crypto;
	if (cryptoProvider && cryptoProvider.subtle && typeof cryptoProvider.subtle.digest === "function") {
		const digest = await cryptoProvider.subtle.digest("SHA-256", source);
		return bytesToHex(new Uint8Array(digest));
	}
	return fallbackSha256(source);
}

function equalBytes(left, right) {
	if (left.byteLength !== right.byteLength) {
		return false;
	}
	for (let index = 0; index < left.byteLength; index += 1) {
		if (left[index] !== right[index]) {
			return false;
		}
	}
	return true;
}

function sortedRoles(roles) {
	return Array.from(roles).sort((left, right) => ROLE_ORDER.indexOf(left) - ROLE_ORDER.indexOf(right));
}

function createAssetCollector(hashBytes) {
	const assetsById = new Map();
	const assetIdByDataUrl = new Map();
	const referencesByRole = ROLE_ORDER.reduce((result, role) => ({ ...result, [role]: 0 }), {});
	let referencedAssetBytes = 0;
	let imageReferenceCount = 0;

	function recordReference(asset, role) {
		asset.roleSet.add(role);
		referencesByRole[role] += 1;
		referencedAssetBytes += asset.byteLength;
		imageReferenceCount += 1;
	}

	async function add(dataUrl, role, owner) {
		const cachedAssetId = assetIdByDataUrl.get(dataUrl);
		if (cachedAssetId) {
			const cachedAsset = assetsById.get(cachedAssetId);
			recordReference(cachedAsset, role);
			return cachedAssetId;
		}

		const { bytes, mimeType } = parseImageDataUrl(dataUrl, owner);
		const sha256 = normalizeHashResult(await hashBytes(bytes, { mimeType, owner, role }));
		const id = `asset-${sha256}`;
		const existingAsset = assetsById.get(id);
		if (existingAsset && !equalBytes(existingAsset.bytes, bytes)) {
			throw new Error(`SHA-256 collision while importing ${owner}.`);
		}
		const asset = existingAsset || {
			id,
			sha256,
			mimeType,
			byteLength: bytes.byteLength,
			fileName: `${sha256}.${extensionForMimeType(mimeType)}`,
			bytes,
			roleSet: new Set()
		};
		if (!existingAsset) {
			assetsById.set(id, asset);
		}
		assetIdByDataUrl.set(dataUrl, id);
		recordReference(asset, role);
		return id;
	}

	function descriptors() {
		return Array.from(assetsById.values()).map((asset) => ({
			id: asset.id,
			sha256: asset.sha256,
			mimeType: asset.mimeType,
			byteLength: asset.byteLength,
			fileName: asset.fileName,
			bytes: asset.bytes,
			roles: sortedRoles(asset.roleSet)
		}));
	}

	function inventory() {
		const uniqueAssetBytes = Array.from(assetsById.values())
			.reduce((total, asset) => total + asset.byteLength, 0);
		return {
			imageReferenceCount,
			uniqueAssetCount: assetsById.size,
			deduplicatedReferenceCount: imageReferenceCount - assetsById.size,
			referencedAssetBytes,
			uniqueAssetBytes,
			deduplicatedBytes: referencedAssetBytes - uniqueAssetBytes,
			referencesByRole: { ...referencesByRole }
		};
	}

	return { add, descriptors, inventory };
}

function sourcePath(parentPath, key, isArrayIndex = false) {
	return isArrayIndex ? `${parentPath}[${key}]` : `${parentPath}.${key}`;
}

async function transformLegacyValue(value, path, assetCollector) {
	if (Array.isArray(value)) {
		const transformed = [];
		for (let index = 0; index < value.length; index += 1) {
			transformed.push(await transformLegacyValue(value[index], sourcePath(path, index, true), assetCollector));
		}
		return transformed;
	}
	if (isObject(value)) {
		const transformed = {};
		for (const key of Object.keys(value)) {
			const field = EMBEDDED_ASSET_FIELDS[key];
			const nextPath = sourcePath(path, key);
			if (field) {
				const embeddedValue = value[key];
				if (embeddedValue === null || embeddedValue === undefined || embeddedValue === "") {
					continue;
				}
				if (Object.prototype.hasOwnProperty.call(value, field.assetIdField)) {
					throw new Error(`${path} already contains reserved field ${field.assetIdField}.`);
				}
				transformed[field.assetIdField] = await assetCollector.add(embeddedValue, field.role, nextPath);
				continue;
			}
			transformed[key] = await transformLegacyValue(value[key], nextPath, assetCollector);
		}
		return transformed;
	}
	if (typeof value === "string" && DATA_URL_PATTERN.test(value)) {
		throw new Error(`${path} contains embedded data outside a supported floorplan image field.`);
	}
	return value;
}

function validateLegacyProject(sourceProject) {
	if (!isObject(sourceProject)) {
		throw new Error("Claude floorplan project must be a parsed JSON object.");
	}
	if (sourceProject.formatVersion !== LEGACY_FORMAT_VERSION) {
		throw new Error(`Unsupported Claude floorplan formatVersion ${sourceProject.formatVersion}; expected 2.`);
	}
	if (!Array.isArray(sourceProject.floors) || sourceProject.floors.length === 0) {
		throw new Error("Claude floorplan project must contain at least one floor.");
	}
	if (!Array.isArray(sourceProject.rules)) {
		throw new Error("Claude floorplan project rules must be an array.");
	}
	sourceProject.floors.forEach((floor, floorIndex) => {
		if (!isObject(floor)) {
			throw new Error(`Claude floorplan floor ${floorIndex} must be an object.`);
		}
		if (!Array.isArray(floor.layers)) {
			throw new Error(`Claude floorplan floor ${floorIndex} layers must be an array.`);
		}
	});
}

function draftAssetRecord(descriptor) {
	return {
		id: descriptor.id,
		sha256: descriptor.sha256,
		mimeType: descriptor.mimeType,
		byteLength: descriptor.byteLength,
		fileName: descriptor.fileName,
		roles: descriptor.roles.slice()
	};
}

/**
 * Converts a parsed Claude formatVersion 2 project into a Drive-ready draft.
 * Binary image payloads are returned separately so callers can upload them
 * before committing the draft JSON.
 */
export async function prepareLegacyFloorplanProject(sourceProject, options = {}) {
	validateLegacyProject(sourceProject);
	const hashBytes = options.hashBytes || options.hasher
		|| ((bytes) => sha256FloorplanAsset(bytes, { crypto: options.crypto }));
	if (typeof hashBytes !== "function") {
		throw new Error("Floorplan asset hasher must be a function.");
	}

	const assetCollector = createAssetCollector(hashBytes);
	const transformedProject = await transformLegacyValue(sourceProject, "$", assetCollector);
	const assets = assetCollector.descriptors();
	const draftAssets = assets.reduce((result, descriptor) => {
		result[descriptor.id] = draftAssetRecord(descriptor);
		return result;
	}, {});
	delete transformedProject.schemaVersion;
	delete transformedProject.source;
	delete transformedProject.assets;
	delete transformedProject.renditions;
	const sourceFileName = basename(options.sourceFileName);
	const draft = {
		schemaVersion: FLOORPLAN_DRAFT_SCHEMA_VERSION,
		source: {
			format: SOURCE_FORMAT,
			formatVersion: LEGACY_FORMAT_VERSION,
			fileName: sourceFileName
		},
		...transformedProject,
		assets: draftAssets
	};
	const validation = validateFloorplanDraft(draft);
	if (!validation.valid) {
		throw new Error(`Unable to prepare floorplan draft: ${validation.errors[0].path} ${validation.errors[0].message}`);
	}
	const assetInventory = assetCollector.inventory();
	return {
		draft,
		assets,
		inventory: {
			projectName: String(sourceProject.projectName || ""),
			sourceFileName,
			floorCount: sourceProject.floors.length,
			basePlanCount: sourceProject.floors.reduce((count, floor) => count + (floor.base ? 1 : 0) + (floor.baseB ? 1 : 0), 0),
			layerCount: sourceProject.floors.reduce((count, floor) => count + floor.layers.length, 0),
			...assetInventory
		}
	};
}

function cloneJsonValue(value) {
	if (Array.isArray(value)) {
		return value.map(cloneJsonValue);
	}
	if (isObject(value)) {
		return Object.keys(value).reduce((result, key) => {
			result[key] = cloneJsonValue(value[key]);
			return result;
		}, {});
	}
	return value;
}

function resourceForAsset(resourcesByAssetId, assetId) {
	if (resourcesByAssetId instanceof Map) {
		return resourcesByAssetId.get(assetId);
	}
	return isObject(resourcesByAssetId) ? resourcesByAssetId[assetId] : undefined;
}

export function attachDriveResourcesToDraft(draft, resourcesByAssetId) {
	if (!isObject(draft) || !isObject(draft.assets)) {
		throw new Error("Floorplan draft assets must be an object before Drive resources can be attached.");
	}
	const missingAssetIds = Object.keys(draft.assets).filter((assetId) => {
		const resource = resourceForAsset(resourcesByAssetId, assetId);
		return !isCanonicalDriveResource(resource);
	});
	if (missingAssetIds.length > 0) {
		throw new Error(`Missing Drive resource references for floorplan assets (canonical id, type, guid, revision, and scope are required): ${missingAssetIds.join(", ")}.`);
	}

	const nextDraft = cloneJsonValue(draft);
	Object.keys(nextDraft.assets).forEach((assetId) => {
		const resource = resourceForAsset(resourcesByAssetId, assetId);
		nextDraft.assets[assetId].resource = cloneJsonValue(resource);
	});
	return nextDraft;
}

function addIssue(target, path, message) {
	target.push({ path, message });
}

function validateDriveResource(resource, path, errors) {
	if (!isObject(resource)) {
		addIssue(errors, path, "must be a Drive resource object");
		return;
	}
	if (!isNonEmptyString(resource.id)) {
		addIssue(errors, `${path}.id`, "must be a non-empty resource id");
	}
	if (resource.type !== DRIVE_RESOURCE_TYPE) {
		addIssue(errors, `${path}.type`, "must identify an external Drive resource");
	}
	if (!isNonEmptyString(resource.guid)) {
		addIssue(errors, `${path}.guid`, "must be a non-empty Drive guid");
	}
	if (!isDriveRevision(resource.revision)) {
		addIssue(errors, `${path}.revision`, "must pin an integer or non-empty string Drive revision");
	}
	if (resource.path !== undefined && !isNonEmptyString(resource.path)) {
		addIssue(errors, `${path}.path`, "must be a non-empty string when provided");
	}
	if (!isNonEmptyString(resource.scope)) {
		addIssue(errors, `${path}.scope`, "must identify the Drive organization scope");
	}
}

function validateTextBoxes(textBoxes, path, errors) {
	if (!Array.isArray(textBoxes)) {
		addIssue(errors, path, "must be an array");
		return;
	}
	textBoxes.forEach((textBox, index) => {
		const textBoxPath = `${path}[${index}]`;
		if (!isObject(textBox)) {
			addIssue(errors, textBoxPath, "must be an object");
			return;
		}
		["x", "y", "w", "h"].forEach((field) => {
			if (typeof textBox[field] !== "number" || !Number.isFinite(textBox[field])) {
				addIssue(errors, `${textBoxPath}.${field}`, "must be a number");
			}
		});
	});
}

function validateDraftImageRecord(record, path, errors, requireArtwork) {
	if (!isObject(record)) {
		addIssue(errors, path, "must be an image record object");
		return;
	}
	if (typeof record.name !== "string") {
		addIssue(errors, `${path}.name`, "must be a string");
	}
	if (typeof record.naturalW !== "number" || !Number.isFinite(record.naturalW) || record.naturalW <= 0) {
		addIssue(errors, `${path}.naturalW`, "must be a positive number");
	}
	if (typeof record.naturalH !== "number" || !Number.isFinite(record.naturalH) || record.naturalH <= 0) {
		addIssue(errors, `${path}.naturalH`, "must be a positive number");
	}
	validateTextBoxes(record.textBoxes, `${path}.textBoxes`, errors);
	if (requireArtwork && (typeof record.imageAssetId !== "string" || !record.imageAssetId)) {
		addIssue(errors, `${path}.imageAssetId`, "must identify the base artwork asset");
	}
}

function inspectDraftValue(value, path, context, ancestors) {
	if (typeof value === "string") {
		if (DATA_URL_PATTERN.test(value)) {
			addIssue(context.errors, path, "must not contain an embedded data URL");
		}
		return;
	}
	if (value === null || typeof value !== "object") {
		return;
	}
	if (ancestors.has(value)) {
		addIssue(context.errors, path, "must not contain a circular reference");
		return;
	}
	ancestors.add(value);
	if (Array.isArray(value)) {
		value.forEach((item, index) => inspectDraftValue(item, sourcePath(path, index, true), context, ancestors));
		ancestors.delete(value);
		return;
	}
	Object.keys(value).forEach((key) => {
		const nextPath = sourcePath(path, key);
		if (EMBEDDED_ASSET_FIELDS[key]) {
			addIssue(context.errors, nextPath, "legacy embedded image fields are not allowed in a floorplan draft");
		}
		if (ASSET_ID_FIELDS.has(key)) {
			if (typeof value[key] !== "string" || !value[key]) {
				addIssue(context.errors, nextPath, "must be a non-empty asset id");
			} else {
				if (!/^asset-[a-f0-9]{64}$/.test(value[key])) {
					addIssue(context.errors, nextPath, "must be a content-addressed floorplan asset id");
				}
				context.referencedAssetIds.add(value[key]);
			}
		}
		inspectDraftValue(value[key], nextPath, context, ancestors);
	});
	ancestors.delete(value);
}

export function validateFloorplanDraft(draft) {
	const errors = [];
	const warnings = [];
	if (!isObject(draft)) {
		return {
			valid: false,
			errors: [{ path: "$", message: "must be an object" }],
			warnings
		};
	}
	if (draft.schemaVersion !== FLOORPLAN_DRAFT_SCHEMA_VERSION) {
		addIssue(errors, "$.schemaVersion", `must equal ${FLOORPLAN_DRAFT_SCHEMA_VERSION}`);
	}
	if (draft.formatVersion !== LEGACY_FORMAT_VERSION) {
		addIssue(errors, "$.formatVersion", "must equal 2");
	}
	if (typeof draft.projectName !== "string") {
		addIssue(errors, "$.projectName", "must be a string");
	}
	if (draft.projectTitle !== undefined && typeof draft.projectTitle !== "string") {
		addIssue(errors, "$.projectTitle", "must be a string");
	}
	if (!Number.isInteger(draft.currentFloorIndex) || draft.currentFloorIndex < 0) {
		addIssue(errors, "$.currentFloorIndex", "must be a non-negative integer");
	}
	if (draft.currentEntry !== "a" && draft.currentEntry !== "b") {
		addIssue(errors, "$.currentEntry", "must equal a or b");
	}
	if (!isObject(draft.source) || draft.source.format !== SOURCE_FORMAT || draft.source.formatVersion !== LEGACY_FORMAT_VERSION) {
		addIssue(errors, "$.source", "must identify a Claude formatVersion 2 project");
	} else if (typeof draft.source.fileName !== "string" || !draft.source.fileName) {
		addIssue(errors, "$.source.fileName", "must be a non-empty string");
	} else {
		Object.keys(draft.source).forEach((field) => {
			if (!SOURCE_FIELDS.has(field)) {
				addIssue(errors, `$.source.${field}`, "is not allowed");
			}
		});
	}
	if (!Array.isArray(draft.floors) || draft.floors.length === 0) {
		addIssue(errors, "$.floors", "must contain at least one floor");
	} else {
		draft.floors.forEach((floor, floorIndex) => {
			if (!isObject(floor)) {
				addIssue(errors, `$.floors[${floorIndex}]`, "must be an object");
				return;
			}
			const floorPath = `$.floors[${floorIndex}]`;
				if (typeof floor.name !== "string" || !floor.name) {
					addIssue(errors, `${floorPath}.name`, "must be a non-empty string");
				}
				if (floor.variantMode !== undefined && !["none", "entry", "local"].includes(floor.variantMode)) {
					addIssue(errors, `${floorPath}.variantMode`, "must equal none, entry, or local");
				}
				if (floor.noNoneGroups !== undefined && (
					!Array.isArray(floor.noNoneGroups)
					|| floor.noNoneGroups.some((group) => typeof group !== "string")
				)) {
					addIssue(errors, `${floorPath}.noNoneGroups`, "must be an array of strings");
				}
				validateDraftImageRecord(floor.base, `${floorPath}.base`, errors, true);
			if (floor.baseB !== null && floor.baseB !== undefined) {
				validateDraftImageRecord(floor.baseB, `${floorPath}.baseB`, errors, true);
			}
			if (!Array.isArray(floor.layers)) {
				addIssue(errors, `${floorPath}.layers`, "must be an array");
			} else {
				floor.layers.forEach((layer, layerIndex) => {
					const layerPath = `${floorPath}.layers[${layerIndex}]`;
					if (!isObject(layer)) {
						addIssue(errors, layerPath, "must be an object");
						return;
					}
					if ((typeof layer.id !== "string" || !layer.id) && !Number.isInteger(layer.id)) {
						addIssue(errors, `${layerPath}.id`, "must be an integer or non-empty string");
					}
					if (typeof layer.name !== "string") {
						addIssue(errors, `${layerPath}.name`, "must be a string");
					}
					validateTextBoxes(layer.textBoxes, `${layerPath}.textBoxes`, errors);
				});
			}
		});
		if (Number.isInteger(draft.currentFloorIndex) && draft.currentFloorIndex >= draft.floors.length) {
			addIssue(errors, "$.currentFloorIndex", "must identify an existing floor");
		}
	}
	if (!Array.isArray(draft.rules)) {
		addIssue(errors, "$.rules", "must be an array");
	} else {
		draft.rules.forEach((rule, ruleIndex) => {
			const rulePath = `$.rules[${ruleIndex}]`;
			if (!isObject(rule)) {
				addIssue(errors, rulePath, "must be an object");
				return;
			}
			["id", "type", "subjectId", "otherId"].forEach((field) => {
				if (!hasOwn(rule, field)) {
					addIssue(errors, `${rulePath}.${field}`, "is required");
				}
			});
			if (hasOwn(rule, "type") && !["requires", "incompatible", "excludes"].includes(rule.type)) {
				addIssue(errors, `${rulePath}.type`, "must equal requires, incompatible, or excludes");
			}
		});
	}
	if (!isObject(draft.assets)) {
		addIssue(errors, "$.assets", "must be an object keyed by asset id");
	}
	if (draft.renditions !== undefined) {
		const renditionsPath = "$.renditions";
		const profile = isObject(draft.renditions) && isObject(draft.renditions.profiles)
			? draft.renditions.profiles.studio
			: null;
		if (!isObject(draft.renditions)) {
			addIssue(errors, renditionsPath, "must be an authoring rendition object");
		} else {
			Object.keys(draft.renditions).forEach((field) => {
				if (!AUTHORING_RENDITIONS_FIELDS.has(field)) {
					addIssue(errors, `${renditionsPath}.${field}`, "is not allowed");
				}
			});
			if (draft.renditions.schemaVersion !== FLOORPLAN_AUTHORING_RENDITIONS_SCHEMA_VERSION) {
				addIssue(errors, `${renditionsPath}.schemaVersion`, `must equal ${FLOORPLAN_AUTHORING_RENDITIONS_SCHEMA_VERSION}`);
			}
			if (draft.renditions.defaultProfile !== "studio") {
				addIssue(errors, `${renditionsPath}.defaultProfile`, "must equal studio");
			}
			if (!isObject(draft.renditions.profiles)) {
				addIssue(errors, `${renditionsPath}.profiles`, "must be an object");
			} else {
				Object.keys(draft.renditions.profiles).forEach((profileId) => {
					if (profileId !== "studio") {
						addIssue(errors, `${renditionsPath}.profiles.${profileId}`, "is not allowed");
					}
				});
				if (!profile) {
					addIssue(errors, `${renditionsPath}.profiles.studio`, "is required");
				}
			}
		}
		if (profile) {
			const profilePath = `${renditionsPath}.profiles.studio`;
			Object.keys(profile).forEach((field) => {
				if (!STUDIO_PROFILE_FIELDS.has(field)) {
					addIssue(errors, `${profilePath}.${field}`, "is not allowed");
				}
			});
			if (!Number.isInteger(profile.maxDimension) || profile.maxDimension < 1) {
				addIssue(errors, `${profilePath}.maxDimension`, "must be a positive integer");
			}
			if (!isObject(profile.assets)) {
				addIssue(errors, `${profilePath}.assets`, "must be an object keyed by canonical asset id");
			} else {
				Object.keys(profile.assets).forEach((assetId) => {
					const rendition = profile.assets[assetId];
					const renditionPath = `${profilePath}.assets.${assetId}`;
					if (!isObject(draft.assets) || !hasOwn(draft.assets, assetId)) {
						addIssue(errors, renditionPath, `references missing canonical floorplan asset ${assetId}`);
					}
					if (!isObject(rendition)) {
						addIssue(errors, renditionPath, "must be a rendition descriptor object");
						return;
					}
					Object.keys(rendition).forEach((field) => {
						if (!RENDITION_DESCRIPTOR_FIELDS.has(field)) {
							addIssue(errors, `${renditionPath}.${field}`, "is not allowed");
						}
					});
					if (!SHA256_PATTERN.test(String(rendition.sha256 || "")) || rendition.id !== `rendition-${rendition.sha256}`) {
						addIssue(errors, `${renditionPath}.sha256`, "must be a lowercase SHA-256 matching the rendition id");
					}
					if (rendition.mimeType !== "image/png") {
						addIssue(errors, `${renditionPath}.mimeType`, "must equal image/png");
					}
					if (!Number.isInteger(rendition.byteLength) || rendition.byteLength < 1) {
						addIssue(errors, `${renditionPath}.byteLength`, "must be a positive integer");
					}
					if (!isNonEmptyString(rendition.fileName)) {
						addIssue(errors, `${renditionPath}.fileName`, "must be a non-empty string");
					}
					["width", "height"].forEach((field) => {
						if (!Number.isInteger(rendition[field]) || rendition[field] < 1) {
							addIssue(errors, `${renditionPath}.${field}`, "must be a positive integer");
						}
					});
					validateDriveResource(rendition.resource, `${renditionPath}.resource`, errors);
				});
			}
		}
	}

	const context = { errors, referencedAssetIds: new Set() };
	inspectDraftValue(draft, "$", context, new Set());
	if (isObject(draft.assets)) {
		Object.keys(draft.assets).forEach((assetId) => {
			const asset = draft.assets[assetId];
			const assetPath = `$.assets.${assetId}`;
			if (!isObject(asset)) {
				addIssue(errors, assetPath, "must be an asset descriptor object");
				return;
			}
			Object.keys(asset).forEach((field) => {
				if (!ASSET_DESCRIPTOR_FIELDS.has(field)) {
					addIssue(errors, `${assetPath}.${field}`, "is not allowed");
				}
			});
			if (asset.id !== assetId) {
				addIssue(errors, `${assetPath}.id`, "must match its assets object key");
			}
			if (!SHA256_PATTERN.test(String(asset.sha256 || "")) || assetId !== `asset-${asset.sha256}`) {
				addIssue(errors, `${assetPath}.sha256`, "must be a lowercase SHA-256 matching the asset id");
			}
			if (typeof asset.mimeType !== "string" || !asset.mimeType.startsWith("image/")) {
				addIssue(errors, `${assetPath}.mimeType`, "must be an image MIME type");
			}
			if (!Number.isInteger(asset.byteLength) || asset.byteLength < 1) {
				addIssue(errors, `${assetPath}.byteLength`, "must be a positive integer");
			}
			if (typeof asset.fileName !== "string" || !asset.fileName) {
				addIssue(errors, `${assetPath}.fileName`, "must be a non-empty string");
			}
			if (
				!Array.isArray(asset.roles)
				|| asset.roles.some((role) => !ROLE_ORDER.includes(role))
				|| new Set(asset.roles).size !== asset.roles.length
			) {
				addIssue(errors, `${assetPath}.roles`, "must contain supported floorplan asset roles");
			}
			if (!asset.resource) {
				addIssue(warnings, `${assetPath}.resource`, "has not been uploaded to Drive yet");
			} else {
				validateDriveResource(asset.resource, `${assetPath}.resource`, errors);
			}
		});
		context.referencedAssetIds.forEach((assetId) => {
			if (!Object.prototype.hasOwnProperty.call(draft.assets, assetId)) {
				addIssue(errors, "$", `references missing floorplan asset ${assetId}`);
			}
		});
		Object.keys(draft.assets).forEach((assetId) => {
			if (!context.referencedAssetIds.has(assetId)) {
				addIssue(warnings, `$.assets.${assetId}`, "is not referenced by the draft");
			}
		});
	}

	return { valid: errors.length === 0, errors, warnings };
}

export function serializeFloorplanDraft(draft) {
	const validation = validateFloorplanDraft(draft);
	if (!validation.valid) {
		throw new Error(`Cannot serialize invalid floorplan draft: ${validation.errors[0].path} ${validation.errors[0].message}.`);
	}
	return `${JSON.stringify(draft, null, 2)}\n`;
}
